import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import axios from 'axios'
import {
  CATEGORY_TABS,
  FilterProductByTab,
  SanitizeFiltersForTab,
  type CategoryTabKey,
  type CategoryTabItem
} from '@/mixins/CategoryMixins.ts'

// --- TypeScript Interfaces ---

export interface PriceOption {
  label: string
  min: number
  max: number
}

export interface Filters {
  sort_type: string | null
  page_limit: number
  search_input: string
  manufacturers: number[] | string[]
  device_types: string[]
  copier_colors: string[]
  speed_minimum: number | null
  speed_maximum: number | null
  active_speed?: string | null
  price_filters: any[]
  price_options: PriceOption[]
  paper_sizes: string[]
  product_categories: string[]
  form_factors: string[]
  capabilities: string[]
  connection_types: string[]
  usage_levels: string[]
  office_sizes: string[]
  print_volumes: string[]
  category_tab?: CategoryTabKey
}

export interface Snackbar {
  visible: boolean
  content: string
  icon?: string
  type?: 'success' | 'error' | 'info' | 'warning'
}

export interface Dialog {
  visible: boolean
  title?: string
  content?: string
}

export interface MenuItem {
  title: string
  value: string
  step: number
}

export interface CopierType {
  value: string | null
  title: string
  icon: string
}

export interface FormAddress {
  street: string
  suite: string
  zip: string
  city: string
  state: string
}

export interface FormContact {
  business_name: string
  ein: string
  first_name: string
  last_name: string
  email: string
  phone: string
  preapproval_id?: number | boolean | null
  preapproval_status?: string | null
  preapproval_grade?: string | null
  preapproval_limit?: number | null
}

export interface Form {
  lease_term: number
  acquisition_type: 'lease' | 'purchase'
  contract_status: 'active' | 'out_of_contract'
  address: FormAddress
  contact: FormContact
}

export interface AssistantChat {
  id?: number | null
  role: 'ai' | 'human' | 'system'
  message: string
  timestamp?: string | null
}

export interface Assistant {
  chats: AssistantChat[]
  prompt: string
  config: any
  theme: string
  visible: boolean
}

// --- Store Definition ---

export const useGlobalsStore = defineStore('globals', () => {

  // --- State ---

  const is_fetching = ref(false)

  const filters = ref<Filters>({
    sort_type: null,
    page_limit: 16,
    search_input: '',
    manufacturers: [],
    device_types: [],
    copier_colors: [],
    speed_minimum: null,
    speed_maximum: null,
    price_filters: [],
    price_options: [
      { label: "$69-$99", min: 69, max: 99 },
      { label: "$100-$199", min: 100, max: 199 },
      { label: "$200+", min: 200, max: 9999 }
    ],
    paper_sizes: [],
    product_categories: [],
    form_factors: [],
    capabilities: [],
    connection_types: [],
    usage_levels: [],
    office_sizes: [],
    print_volumes: [],
    category_tab: 'printers',
  })

  const snackbar = ref<Snackbar>({
    visible: false,
    content: '',
    icon: '',
    type: 'success',
  })

  const dialog = ref<Dialog>({
    visible: false,
    title: '',
    content: '',
  })

  const sort_types = ref([
    { value: 'ascending', title: 'Sort: Name (A-Z)' },
    { value: 'descending', title: 'Sort: Name (Z-A)' },
    { value: 'speed-low-high', title: 'Sort: Speed (Low to High)' },
    { value: 'speed-high-low', title: 'Sort: Speed (High to Low)' },
    { value: 'newest', title: 'Sort: Newest First' },
    { value: 'oldest', title: 'Sort: Oldest First' },
    { value: null, title: 'Sort: Default Order' }
  ])

  const menu_items = ref<MenuItem[]>([
    { title: 'Choose a copier type', value: 'client_dashboard', step: 1 },
    { title: 'Estimated monthly prints', value: 'client_contracts', step: 2 },
    { title: 'Configure your copier', value: 'client_agents', step: 3 },
    { title: 'Finalize your Quote', value: 'client_agents', step: 4 },
  ])

  const copier_types = ref<CopierType[]>([
    { value: 'B&W', title: 'Black & White', icon: 'mdi-invert-colors-off' },
    { value: 'Color', title: 'Color', icon: 'mdi-invert-colors' },
    { value: null, title: 'Not Sure', icon: 'mdi-help-circle' }
  ])

  const datasets = ref<any[]>([])
  const accessories = ref<any[]>([])
  const categories = ref<any[]>([])
  const lease_terms = ref<any[]>([])
  const product_pricing = ref<any[]>([])
  const cart_items = ref<any[]>([])
  const selected_copier = ref<any | null>(null)

  const form = ref<Form>({
    lease_term: 60,
    acquisition_type: 'lease',
    contract_status: 'out_of_contract',
    address: {
      street: '',
      suite: '',
      zip: '',
      city: '',
      state: ''
    },
    contact: {
      business_name: '',
      ein: '',
      first_name: '',
      last_name: '',
      email: '',
      phone: ''
    }
  })

  const products = ref<any[]>([])
  const manufacturers = ref<any[]>([])
  const product_reviews = ref<any[]>([])

  const printer_categories = ref<any[]>([])
  const paper_sizes = ref<any[]>([])
  const connection_types = ref<any[]>([])
  const media_types = ref<any[]>([])
  const form_factors = ref<any[]>([])
  const capabilities = ref<any[]>([])
  const usage_levels = ref<any[]>([])

  const configurator = ref(false)
  const config_drawer = ref(true)
  const config_fullscreen = ref(false)
  const view_product = ref<any | null>(null)
  const zoom_level = ref(1)
  const redo_steps = ref<any[]>([])

  const cart_modal = ref(false)
  const form_success = ref(false)
  const review_modal = ref(false)
  const active_form = ref<string | null>(null)
  const is_loading = ref(false)
  const addtocart_trigger = ref(0)

  const states = ref([
    "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "DC", "FL",
    "GA", "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME",
    "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH",
    "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI",
    "SC", "SD", "TN", "TX", "UT", "VT", "VI", "VA", "WA", "WV",
    "WI", "WY",
  ])

  const manufacturers_allowed = ref<number[]>([])
  const free_shipping = ref<boolean>(
    Boolean(window.catalog_config?.free_shipping === true || (window.catalog_config as any)?.free_shipping === 'true' || (window.catalog_config as any)?.free_shipping === 1 || (window.catalog_config as any)?.free_shipping === '1')
  )
  const run_preapprovals = ref<boolean>(
    Boolean(window.catalog_config?.run_preapprovals === true || (window.catalog_config as any)?.run_preapprovals === 'true' || (window.catalog_config as any)?.run_preapprovals === 1 || (window.catalog_config as any)?.run_preapprovals === '1')
  )
  const partner_identifier = ref<string | number>(
    (window.catalog_config as any)?.partner_identifier ?? ''
  )
  const catalog_page_title = ref("Products Catalog")
  const is_mobile = ref(false)

  const assistant = ref<Assistant>({
    chats: [],
    prompt: "",
    config: null,
    theme: "light",
    visible: true,
  })

  const form_step = ref(1)
  const configurator_step = ref<'configurator' | 'summary' | 'address' | 'contact' | 'result'>('configurator')
  const color_scheme = ref('light')

  // --- Dynamic Refs Map for SetAnyState Mutation Mirror ---

  const storeRefsMap: Record<string, any> = {
    is_fetching,
    configurator_step,
    filters,
    snackbar,
    dialog,
    datasets,
    accessories,
    categories,
    lease_terms,
    product_pricing,
    cart_items,
    selected_copier,
    form,
    products,
    manufacturers,
    product_reviews,
    printer_categories,
    paper_sizes,
    connection_types,
    media_types,
    form_factors,
    capabilities,
    usage_levels,
    configurator,
    config_drawer,
    config_fullscreen,
    view_product,
    zoom_level,
    redo_steps,
    cart_modal,
    form_success,
    review_modal,
    active_form,
    is_loading,
    addtocart_trigger,
    manufacturers_allowed,
    free_shipping,
    run_preapprovals,
    partner_identifier,
    catalog_page_title,
    is_mobile,
    assistant,
    form_step,
    color_scheme
  }

  // --- Actions ---

  /**
   * Fetches the entire showroom catalog payload from the WordPress REST API endpoint
   * (`/wp-json/1-800-products/v1/showroom-data`), applies frontend configuration rules,
   * deduplicates products by slug, and hydrates the reactive Pinia store state.
   *
   * Key Operations:
   * 1. **Configuration Sync:** Reads injected `window.catalog_config` (from `settings.json`)
   *    to set shipping flags, allowed manufacturers, catalog page title, and pagination limits.
   * 2. **Product Filtering & Slug Deduplication:**
   *    - Restricts products to `manufacturers_allowed` (if defined).
   *    - Excludes specific product IDs defined in `product_exclusions`.
   *    - Flags matching product IDs in `best_sellers` with `best_seller: true`.
   *    - Enforces **unique product slugs** via `seen_slugs` set to eliminate duplicate listings.
   * 3. **Taxonomy & Metadata Hydration:** Populates manufacturer brands, category filters,
   *    lease matrix terms, pricing tiers, specifications (paper size, connectivity, capabilities),
   *    and customer reviews.
   * 4. **Error Handling:** Triggers global snackbar alerts if the API returns an error or network fails.
   */
  async function FetchCatalogData() {
    is_fetching.value = true
    try {
      const res = await axios.get('/wp-json/1-800-products/v1/showroom-data')
      if (res.data.result) {
        const settings = window.catalog_config
        if (settings) {
          free_shipping.value = Boolean(settings.free_shipping === true || (settings as any).free_shipping === 'true' || (settings as any).free_shipping === 1 || (settings as any).free_shipping === '1')
          manufacturers_allowed.value = settings.manufacturers_allowed
          catalog_page_title.value = settings.catalog_page_title

          const select_products: any[] = []
          const seen_slugs = new Set<string>()

          if (res.data.products) {
            res.data.products.forEach((product: any) => {
              const allowed_manufacturers = Array.isArray(settings.manufacturers_allowed) ? settings.manufacturers_allowed : []
              const product_exclusions = Array.isArray(settings.product_exclusions) ? settings.product_exclusions : []
              const best_sellers = Array.isArray(settings.best_sellers) ? settings.best_sellers.map(Number) : []

              const is_allowed = allowed_manufacturers.length === 0 || allowed_manufacturers.includes(Number(product.manufacturer_id))
              const is_excluded = product_exclusions.includes(Number(product.id))

              // Only import products with unique slug
              const slug = String(product.slug || '').trim().toLowerCase()
              const is_duplicate_slug = slug !== '' && seen_slugs.has(slug)

              if (is_allowed && !is_excluded && !is_duplicate_slug) {
                if (slug !== '') {
                  seen_slugs.add(slug)
                }
                select_products.push({
                  ...product,
                  best_seller: best_sellers.includes(Number(product.id))
                })
              }
            })
          }
          products.value = select_products

          // Update page_limit filters
          filters.value.page_limit = Number(settings.items_per_page) || 16
        } else {
          // Fallback if settings are not defined (filter unique slugs)
          const raw_products = res.data.products || []
          const seen_slugs = new Set<string>()
          products.value = raw_products.filter((p: any) => {
            const slug = String(p.slug || '').trim().toLowerCase()
            if (!slug) return true
            if (seen_slugs.has(slug)) return false
            seen_slugs.add(slug)
            return true
          })
        }

        manufacturers.value = res.data.manufacturers || []
        categories.value = res.data.categories || []
        lease_terms.value = res.data.lease_terms || []
        product_pricing.value = res.data.product_pricing || []
        product_reviews.value = res.data.product_reviews || []

        printer_categories.value = res.data.printer_categories || []
        paper_sizes.value = res.data.paper_sizes || []
        connection_types.value = res.data.connection_types || []
        media_types.value = res.data.media_types || []
        form_factors.value = res.data.form_factors || []
        capabilities.value = res.data.capabilities || []
        usage_levels.value = res.data.usage_levels || []
      } else {
        SetSnackbar({
          visible: true,
          content: res.data.errors || 'Unknown error occurred.',
          type: 'error',
          icon: 'mdi-alert-circle-outline'
        })
      }
    } catch (err: any) {
      SetError(err.message || 'An error occurred while fetching catalog data.')
    } finally {
      is_fetching.value = false
    }
  }

  function SetFilters(params: Filters) {
    filters.value = params
  }

  function SetCategoryTab(tab: CategoryTabKey) {
    filters.value = SanitizeFiltersForTab(filters.value, tab)
  }

  function SetFormStep(action: number | 'prev' | 'next') {
    if (typeof action === 'number') {
      form_step.value = action
    } else if (action === 'prev') {
      if (form_step.value > 1) {
        form_step.value--
      }
    } else {
      form_step.value++
    }
  }

  function SetCartItems(items: any[]) {
    cart_items.value = items
  }

  function SetSelectedCopier(copier: any) {
    selected_copier.value = copier
  }

  function SetSnackbar(params: Snackbar) {
    snackbar.value = params
  }

  function SetDialog(params: Dialog) {
    dialog.value = params
  }

  function SetError(message: string) {
    SetSnackbar({
      visible: true,
      content: message,
      type: 'error',
      icon: 'mdi-alert-circle-outline'
    })
  }

  function SetSuccess(message: string) {
    SetSnackbar({
      visible: true,
      content: message,
      type: 'success',
      icon: 'mdi-check-circle'
    })
  }

  function SetAnyState({ target, value }: { target: string; value: any }) {
    if (target in storeRefsMap) {
      storeRefsMap[target].value = value
    }
  }

  function ToggleChatUI() {
    assistant.value.visible = !assistant.value.visible
  }

  function AddChat(chat: AssistantChat) {
    assistant.value.chats.push(chat)
  }

  function ClearQuoteValues() {
    form.value = {
      lease_term: 60,
      acquisition_type: 'lease',
      contract_status: 'out_of_contract',
      address: {
        street: '',
        suite: '',
        zip: '',
        city: '',
        state: ''
      },
      contact: {
        business_name: '',
        ein: '',
        first_name: '',
        last_name: '',
        email: '',
        phone: ''
      }
    }
  }

  // --- Ported from CommonMixins.js ---

  const ConfigShopURL = computed(() => 'https://1800toners.com')

  const ConfigTopbarColor = computed(() => {
    return window.catalog_config?.colors?.topbar || '#ffffff'
  })

  const ConfigShowPricing = computed(() => {
    return window.catalog_config?.show_pricing ?? false
  })

  const ConfigModalsColor = computed(() => {
    return window.catalog_config?.colors?.modals || '#1565C0'
  })

  const ConfigSinglePageUrl = computed(() => {
    const url = window.catalog_config?.single_page_url || '/product'
    return url.replace(/\/$/, '')
  })

  const ConfigElementsColor = computed(() => {
    return window.catalog_config?.colors?.buttons || '#1565C0'
  })

  const ConfigShowShopBanner = computed(() => {
    const val: any = window.catalog_config?.show_shop_banner
    return Boolean(val === true || val === 'true' || val === 1 || val === '1')
  })

  const ConfigShowFeaturedCards = computed(() => {
    const val: any = window.catalog_config?.show_featured_cards
    return Boolean(val === true || val === 'true' || val === 1 || val === '1')
  })

  const ConfigChatEnabled = computed(() => {
    return !!(assistant.value.config && assistant.value.config.enabled)
  })

  /**
   * Category tabs that have at least one product matching the current site's allowed catalog.
   * If a site does not carry brands for a particular tab (0 items), that tab is excluded.
   */
  const available_category_tabs = computed<CategoryTabItem[]>(() => {
    if (!products.value || products.value.length === 0) {
      return CATEGORY_TABS
    }
    return CATEGORY_TABS.filter(tab => {
      return products.value.some((product: any) => FilterProductByTab(product, tab.key))
    })
  })

  function SetState(target: string, value: any) {
    SetAnyState({ target, value })
  }

  function ConfigureProduct(product: any) {
    SetSelectedCopier(product)
    SetState('configurator', true)
  }

  function OpenHelpWindow() {
    if (ConfigChatEnabled.value) {
      ToggleChatUI()
    }
  }

  function FormSelector() {
    let the_copier = null
    if (selected_copier.value) {
      the_copier = selected_copier.value
    } else if (cart_items.value.length > 0) {
      the_copier = cart_items.value[cart_items.value.length - 1].product
    }

    if (the_copier) {
      const ff = String(the_copier.form_factor || '').toLowerCase()
      const cat = String(the_copier.printer_category || '').toLowerCase()
      if (the_copier.manufacturer_id == 9) {
        SetState('active_form', 'mailing-form')
      } else if (ff == 'wide-format') {
        SetState('active_form', 'wide-format-form')
      } else if (cat.includes('phone')) {
        SetState('active_form', 'phone-form')
      } else if (cat == 'scanner') {
        SetState('active_form', 'scanner-form')
      } else {
        SetState('active_form', 'default-form')
      }
    }
  }

  function SetAiAssistantConfig() {
    const AiAssistant = { ...assistant.value }
    AiAssistant.config = window.ai_chat_config
    AiAssistant.visible = false
    SetState('assistant', AiAssistant)
  }

  function ProductImage(product: any) {
    const url = (import.meta.env.VITE_SERVERAPI_URL || '') + '/storage/uploads/products'
    if (product?.product_image != null && product.product_image.length > 0) {
      return `${url}/product-images/${product.product_image}`
    } else {
      return `${url}/product-images/_placeholder_.png`
    }
  }

  return {
    // State
    is_fetching,
    filters,
    snackbar,
    dialog,
    sort_types,
    menu_items,
    copier_types,
    datasets,
    accessories,
    categories,
    lease_terms,
    product_pricing,
    cart_items,
    selected_copier,
    form,
    products,
    manufacturers,
    product_reviews,
    printer_categories,
    paper_sizes,
    connection_types,
    media_types,
    form_factors,
    capabilities,
    usage_levels,
    configurator,
    config_drawer,
    config_fullscreen,
    view_product,
    zoom_level,
    redo_steps,
    cart_modal,
    form_success,
    review_modal,
    active_form,
    is_loading,
    addtocart_trigger,
    states,
    manufacturers_allowed,
    free_shipping,
    run_preapprovals,
    partner_identifier,
    catalog_page_title,
    is_mobile,
    assistant,
    form_step,
    configurator_step,
    color_scheme,

    // Actions
    FetchCatalogData,
    SetFilters,
    SetCategoryTab,
    SetFormStep,
    SetCartItems,
    SetSelectedCopier,
    SetSnackbar,
    SetDialog,
    SetError,
    SetSuccess,
    SetAnyState,
    ToggleChatUI,
    AddChat,
    ClearQuoteValues,

    // Configs & Mixin helpers
    ConfigShopURL,
    ConfigTopbarColor,
    ConfigShowPricing,
    ConfigModalsColor,
    ConfigSinglePageUrl,
    ConfigElementsColor,
    ConfigShowShopBanner,
    ConfigShowFeaturedCards,
    ConfigChatEnabled,
    SetState,
    OpenHelpWindow,
    FormSelector,
    SetAiAssistantConfig,
    ProductImage,
    ConfigureProduct,
    available_category_tabs
  }
}, {
  persist: {
    pick: [
      'form',
      'filters',
      'assistant',
      'cart_items',
      'configurator',
      'color_scheme',
      'selected_copier'
    ]
  }
})
